import { useMemo, useRef, useState } from 'react';
import { FieldError } from '../LaddboxForm';
import { labelClass } from './schema';

export function ImageDropField({
  name,
  value,
  errors,
  onBlur,
  onChange,
}: {
  name: string;
  value: File | undefined;
  errors: string[];
  onBlur: () => void;
  onChange: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasError = errors.length > 0;
  const previewUrl = useMemo(
    () => (value ? URL.createObjectURL(value) : null),
    [value],
  );

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onChange(file);
  };

  return (
    <div>
      <label htmlFor="image" className={labelClass}>
        Sticker
      </label>
      <div className="relative group">
        <div
          role="button"
          tabIndex={0}
          aria-label="Ladda upp bild"
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) =>
            (e.key === 'Enter' || e.key === ' ') &&
            fileInputRef.current?.click()
          }
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          onBlur={onBlur}
          className={[
            'w-full rounded border-2 transition-colors cursor-pointer select-none overflow-hidden',
            previewUrl
              ? 'border-kia-gray'
              : 'border-dashed px-4 py-8 text-center text-sm',
            !previewUrl &&
              (isDragging
                ? 'border-black bg-gray-50'
                : hasError
                  ? 'border-red-400'
                  : 'border-kia-gray hover:border-black'),
          ].join(' ')}
        >
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt={value?.name}
                className="w-full max-h-64 object-contain block"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                <span className="text-white text-sm font-medium">Byt bild</span>
              </div>
            </>
          ) : (
            <>
              <span className="font-medium">Ladda upp din Sticker här</span>
              <span className="text-gray-500"> eller dra och släpp här</span>
            </>
          )}
        </div>
        {previewUrl && (
          <button
            type="button"
            onClick={() => {
              onChange(undefined as unknown as File);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="absolute top-2 right-2 bg-white/90 rounded-full w-7 h-7 flex items-center justify-center text-sm leading-none hover:bg-white transition-colors"
            aria-label="Ta bort bild"
          >
            ×
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        className="sr-only"
        type="file"
        accept="image/png,image/jpeg,image/bmp,image/gif,image/webp"
        id={name}
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <FieldError errors={errors} />
    </div>
  );
}
